/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import { openDB, DBSchema, IDBPDatabase } from 'idb';
import type { Note, NoteHistoryRecord, TokenUsageRecord } from '../types/types';

// Simple encryption utilities for local data protection
class DataEncryption {
  private dbService: DBService;

  constructor(dbService: DBService) {
    this.dbService = dbService;
  }

  private async getUserKey(): Promise<CryptoKey | null> {
    try {
      const user = await this.dbService.getCurrentUser();
      if (!user || !user.sub) return null;

      // Use user's Google ID as the base for encryption key
      const keyMaterial = await crypto.subtle.importKey(
        'raw',
        new TextEncoder().encode(user.sub),
        'PBKDF2',
        false,
        ['deriveBits', 'deriveKey']
      );

      return await crypto.subtle.deriveKey(
        {
          name: 'PBKDF2',
          salt: new TextEncoder().encode('ConFabulatorSalt'),
          iterations: 100000,
          hash: 'SHA-256'
        },
        keyMaterial,
        { name: 'AES-GCM', length: 256 },
        false,
        ['encrypt', 'decrypt']
      );
    } catch (error) {
      console.warn('Failed to generate encryption key:', error);
      return null;
    }
  }

  async encrypt(data: string): Promise<string> {
    const key = await this.getUserKey();
    if (!key) return btoa(data); // Fallback to base64 if no key

    try {
      const iv = crypto.getRandomValues(new Uint8Array(12));
      const encodedData = new TextEncoder().encode(data);

      const encrypted = await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv: iv },
        key,
        encodedData
      );

      const combined = new Uint8Array(iv.length + encrypted.byteLength);
      combined.set(iv);
      combined.set(new Uint8Array(encrypted), iv.length);

      return btoa(String.fromCharCode(...combined));
    } catch (error) {
      console.warn('Encryption failed, using base64 fallback:', error);
      return btoa(data);
    }
  }

  async decrypt(encryptedData: string): Promise<string> {
    const key = await this.getUserKey();
    if (!key) return atob(encryptedData); // Fallback to base64 if no key

    try {
      const combined = new Uint8Array(
        atob(encryptedData).split('').map(char => char.charCodeAt(0))
      );

      const iv = combined.slice(0, 12);
      const data = combined.slice(12);

      const decrypted = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: iv },
        key,
        data
      );

      return new TextDecoder().decode(decrypted);
    } catch (error) {
      console.warn('Decryption failed, using base64 fallback:', error);
      return atob(encryptedData);
    }
  }
}

// Define the database schema using the DBSchema interface from 'idb'.
interface AppDBSchema extends DBSchema {
  notes: {
    key: string;
    value: Note;
    indexes: { 'timestamp': number; 'driveFileId': string };
  };
  noteHistory: {
    key: number;
    value: NoteHistoryRecord;
    indexes: { 'noteId_timestamp': [string, number] };
  };
  settings: {
    key: string;
    value: { key: string, value: any };
  };
  tokenUsageLog: {
    key: number;
    value: TokenUsageRecord;
    indexes: { 'timestamp': number };
  };
}

const DB_NAME = 'DictationAppDB';
const DB_VERSION = 3;

/**
 * A service class to encapsulate all IndexedDB interactions.
 */
export class DBService {
  private dbPromise: Promise<IDBPDatabase<AppDBSchema>>;
  private currentUser: any = null;
  private encryption: DataEncryption;

  constructor() {
    this.encryption = new DataEncryption(this);
    this.dbPromise = openDB<AppDBSchema>(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion) {
        switch (oldVersion) {
          case 0: {
            // New DB setup
            const notesStore = db.createObjectStore('notes', { keyPath: 'id' });
            notesStore.createIndex('timestamp', 'timestamp');
            notesStore.createIndex('driveFileId', 'driveFileId');
            db.createObjectStore('settings', { keyPath: 'key' });
          }
          // fall through
          case 1: {
            // Upgrade from v1 to v2
            const historyStore = db.createObjectStore('noteHistory', { keyPath: 'historyId', autoIncrement: true });
            historyStore.createIndex('noteId_timestamp', ['noteId', 'snapshot.modifiedTimestamp']);
          }
          // fall through
          case 2: {
            // Upgrade from v2 to v3
            const tokenStore = db.createObjectStore('tokenUsageLog', { keyPath: 'id', autoIncrement: true });
            tokenStore.createIndex('timestamp', 'timestamp');
          }
        }
      },
    });
  }

  public setCurrentUser(user: any): void {
    this.currentUser = user;
  }

  public async getCurrentUser(): Promise<any> {
    return this.currentUser;
  }

  // --- Note Methods ---

  async getNote(id: string): Promise<Note | undefined> {
    const storedNote = await (await this.dbPromise).get('notes', id);

    if (!storedNote) return undefined;

    // Decrypt if the note is encrypted
    if (storedNote._encrypted && storedNote._data) {
      try {
        const decryptedData = await this.encryption.decrypt(storedNote._data);
        return JSON.parse(decryptedData);
      } catch (error) {
        console.warn('Failed to decrypt note, returning unencrypted version:', error);
        return storedNote;
      }
    }

    return storedNote;
  }

  async getAllNotes(): Promise<Note[]> {
    // Get all notes and sort them by timestamp descending (newest first)
    const notes = await (await this.dbPromise).getAllFromIndex('notes', 'timestamp');
    return notes.reverse();
  }
  
  async saveNote(note: Note): Promise<string> {
    let noteToSave = note;

    // Encrypt note content if user is authenticated
    if (this.currentUser) {
      try {
        const noteString = JSON.stringify(note);
        const encryptedNote = await this.encryption.encrypt(noteString);
        noteToSave = { ...note, _encrypted: true, _data: encryptedNote };
      } catch (error) {
        console.warn('Failed to encrypt note, saving unencrypted:', error);
      }
    }

    return (await this.dbPromise).put('notes', noteToSave);
  }
  
  async deleteNote(id: string): Promise<void> {
    return (await this.dbPromise).delete('notes', id);
  }
  
  async getMostRecentNote(): Promise<Note | undefined> {
    const allNotes = await this.getAllNotes(); // Already sorted newest first
    return allNotes.length > 0 ? allNotes[0] : undefined;
  }

  async clearAllNotes(): Promise<void> {
    const db = await this.dbPromise;
    const tx = db.transaction(['notes', 'noteHistory'], 'readwrite');

    // Clear all notes
    await tx.objectStore('notes').clear();

    // Clear all note history
    await tx.objectStore('noteHistory').clear();

    await tx.done;
  }

  async clearAllData(): Promise<void> {
    const db = await this.dbPromise;
    const tx = db.transaction(['notes', 'noteHistory', 'settings', 'tokenUsageLog'], 'readwrite');

    // Clear all data
    await tx.objectStore('notes').clear();
    await tx.objectStore('noteHistory').clear();
    await tx.objectStore('settings').clear();
    await tx.objectStore('tokenUsageLog').clear();

    await tx.done;
  }

  // --- History Methods ---

  async addNoteHistory(note: Note): Promise<number> {
    const record: NoteHistoryRecord = {
        noteId: note.id,
        snapshot: JSON.parse(JSON.stringify(note)) // Simple deep copy
    };
    return (await this.dbPromise).put('noteHistory', record);
  }

  async getNoteHistory(noteId: string): Promise<NoteHistoryRecord[]> {
    const db = await this.dbPromise;
    const records = await db.getAllFromIndex('noteHistory', 'noteId_timestamp', IDBKeyRange.bound([noteId, 0], [noteId, Infinity]));
    return records.reverse(); // The index gives us ascending, so we reverse for newest first
  }

  async getLatestNoteHistory(noteId: string): Promise<NoteHistoryRecord | undefined> {
    const db = await this.dbPromise;
    const index = db.transaction('noteHistory').store.index('noteId_timestamp');
    const cursor = await index.openCursor(IDBKeyRange.bound([noteId, 0], [noteId, Infinity]), 'prev');
    return cursor?.value;
  }

  async deleteNoteHistoryForNote(noteId: string): Promise<void> {
    const db = await this.dbPromise;
    const tx = db.transaction('noteHistory', 'readwrite');
    const index = tx.store.index('noteId_timestamp');
    const range = IDBKeyRange.bound([noteId, 0], [noteId, Infinity]);
    let cursor = await index.openCursor(range);
    while (cursor) {
      cursor.delete();
      cursor = await cursor.continue();
    }
    await tx.done;
  }

  // --- Settings Methods ---

  async getSetting<T>(key: string): Promise<T | undefined> {
    const result = await (await this.dbPromise).get('settings', key);
    return result?.value;
  }

  async setSetting(key: string, value: any): Promise<string> {
    return (await this.dbPromise).put('settings', { key, value });
  }

  // --- Token Usage Log Methods ---

  async logTokenUsage(record: Omit<TokenUsageRecord, 'id'>): Promise<number> {
    return (await this.dbPromise).put('tokenUsageLog', record as TokenUsageRecord);
  }

  async getAllTokenUsageRecords(): Promise<TokenUsageRecord[]> {
    const records = await (await this.dbPromise).getAllFromIndex('tokenUsageLog', 'timestamp');
    return records.reverse(); // Newest first
  }
}